import React, { useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import { Map, View, Feature, Overlay } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import { Style, Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import TileGrid from 'ol/tilegrid/TileGrid';
import GeoJSON from 'ol/format/GeoJSON';
import { register } from 'ol/proj/proj4';
import { get as getProjection } from 'ol/proj';
import proj4 from 'proj4';
import { gebiedStijl, maakDiagonaleStrepen } from '../util/gebiedstijl';

proj4.defs(
  'EPSG:28992',
  '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 ' +
  '+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel ' +
  '+towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 ' +
  '+units=m +no_defs'
);
register(proj4);
const rdProjection = getProjection('EPSG:28992');
rdProjection.setExtent([-285401.92, 22598.08, 595401.92, 903401.92]);

const RESOLUTIONS = [
  3440.64, 1720.32, 860.16, 430.08, 215.04, 107.52, 53.76, 26.88,
  13.44, 6.72, 3.36, 1.68, 0.84, 0.42, 0.21,
];
const ORIGIN = [-285401.92, 903401.92];
const rdTileGrid = new TileGrid({ origin: ORIGIN, resolutions: RESOLUTIONS, tileSize: 256 });

// PDOK BGT achtergrondvisualisatie heeft pas vanaf zoom 12 zinvolle inhoud
const BGT_MIN_ZOOM = 12;

const ATTRIBUTION = 'Kaartdata: <a href="https://www.kadaster.nl">Kadaster</a> via <a href="https://www.pdok.nl">PDOK</a>';

const makeSource = (path, layer, ext = 'png') => new XYZ({
  url: `https://service.pdok.nl/${path}/${layer}/EPSG:28992/{z}/{x}/{y}.${ext}`,
  projection: rdProjection,
  tileGrid: rdTileGrid,
  attributions: ATTRIBUTION,
});

/* Markerstijlen.

   Oorspronkelijk kende deze kaart twee toestanden: blauw en grijs, via de vlag
   `afgedicht`. Het puttenpaneel heeft er meer nodig, en die volgen bewust de
   taal die de melder in het meldingsformulier al ziet: blauw is een put die er
   al is, oranje is een put die nog geboord moet worden.

     gekoppeld        gevuld blauw   — bestaat al én staat in het register
     ongeregistreerd  hol blauw      — bestaat al, staat nog niet in het register
     nieuw            gevuld oranje  — moet nog geboord worden
     afgedicht        gevuld grijs   — bestaande betekenis, niet aangeraakt

   Hol versus gevuld doet het werk dat kleur alleen niet kan: wie kleuren slecht
   onderscheidt ziet nog steeds twee verschillende dingen.

   `soort` is optioneel; zonder die vlag gedraagt de kaart zich precies als
   voorheen, dus de andere schermen die RDKaart gebruiken blijven ongemoeid. */
const MARKER_KLEUR = {
  gekoppeld: '#0071e3',
  ongeregistreerd: '#0071e3',
  nieuw: '#ff9500',
  onbeslist: '#b25000',   // nog niet beslist welke registerput het is
  afgedicht: '#86868b',
  // Hoort er nu nog bij, maar gaat er bij dit besluit uit.
  verwijderd: '#86868b',
};

const markerStyle = (afgedicht = false, soort = null, geselecteerd = false) => {
  const sleutel = soort || (afgedicht ? 'afgedicht' : 'gekoppeld');
  const kleur = MARKER_KLEUR[sleutel] || MARKER_KLEUR.gekoppeld;
  const hol = sleutel === 'ongeregistreerd' || sleutel === 'onbeslist';
  return new Style({
    image: new CircleStyle({
      radius: geselecteerd ? 9.5 : 7,
      fill: new Fill({ color: hol ? '#ffffff' : kleur }),
      stroke: new Stroke({
        color: geselecteerd ? '#1d1d1f' : (hol ? kleur : '#ffffff'),
        width: hol && !geselecteerd ? 3.5 : 3,
      }),
    }),
    zIndex: geselecteerd ? 10 : 1,
  });
};

/* Het putnummer naast de stip. Zelfde behandeling als op de publieke
   inrichtingspagina (backend/templates/statisch/inrichting.js): vet, klein, op
   een wit vlakje zodat het leesbaar blijft op gras en op een luchtfoto, en
   rechts van de marker zodat het de stip zelf niet afdekt.

   De labels staan in een eigen laag met declutter aan. Botsen twee nummers,
   dan valt er een weg in plaats van dat ze over elkaar heen komen -- bij twee
   putten naast elkaar op een erf gebeurt dat zo. Declutter kan alleen hele
   features verbergen, en daarom staan de labels los van de markers: anders zou
   een verborgen label ook de stip meenemen. */
const labelStijl = (tekst) => new Style({
  text: new Text({
    text: tekst,
    font: "600 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
    fill: new Fill({ color: '#1d1d1f' }),
    backgroundFill: new Fill({ color: 'rgba(255,255,255,0.88)' }),
    padding: [2, 5, 2, 5],
    offsetX: 15,
    textAlign: 'left',
  }),
});

/* Vier streepjes die naar het punt wijzen, met een gat in het midden zodat je
   de put eronder blijft zien. Wit eronder, bijna-zwart erop: leesbaar op gras,
   op asfalt en op een luchtfoto. Rood zou hier verkeerd lezen — dat betekent in
   deze schermen "hier mag het niet". */
function tekenDraadkruis(ctx, x, y, schaal, binnen, buiten) {
  const richtingen = [[0, -1], [0, 1], [-1, 0], [1, 0]];
  const armen = (kleur, breedte) => {
    ctx.strokeStyle = kleur;
    ctx.lineWidth = breedte * schaal;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const [dx, dy] of richtingen) {
      ctx.moveTo(x + dx * binnen * schaal, y + dy * binnen * schaal);
      ctx.lineTo(x + dx * buiten * schaal, y + dy * buiten * schaal);
    }
    ctx.stroke();
  };
  armen('rgba(255,255,255,0.95)', 5.5);
  armen('#1d1d1f', 2.5);
}

/* Ruim om de marker heen, zonder middenstip: de gekleurde stip eronder moet
   zijn werk blijven doen. */
const draadkruisStijl = () => new Style({
  renderer: (coords, state) => {
    const [x, y] = coords;
    tekenDraadkruis(state.context, x, y, state.pixelRatio, 14, 22);
  },
  zIndex: 20,
});

// Cache: per type 1x patroon aanmaken (bij eerste gebruik)
const _patternCache = {};
function getFillKleur(type) {
  const s = gebiedStijl(type);
  if (s.pattern === 'diagonalStripes') {
    if (!_patternCache[type]) {
      _patternCache[type] = maakDiagonaleStrepen(s.stroke);
    }
    if (_patternCache[type]) return _patternCache[type];
  }
  return s.fillRgba;
}

const gebiedStyle = (type) => {
  const s = gebiedStijl(type);
  return new Style({
    fill: new Fill({ color: getFillKleur(type) }),
    stroke: new Stroke({ color: s.stroke, width: 1 }),
  });
};

/* Hetzelfde vlak, even naar voren gehaald: dikkere rand en bovenop de andere
   overlays. Voor als je buiten de kaart aanwijst welk gebied je bedoelt — een
   label in een paneel, bijvoorbeeld — en het vlak zich moet melden. De vulling
   blijft gelijk; alleen de omtrek verandert, zodat het gebied niet ineens een
   andere kleur lijkt te hebben. */
const gebiedStyleBenadrukt = (type) => {
  const s = gebiedStijl(type);
  return new Style({
    fill: new Fill({ color: getFillKleur(type) }),
    stroke: new Stroke({ color: s.stroke, width: 3.5 }),
    zIndex: 5,
  });
};

// Zet een (Multi)Polygon om in zijn complement binnen een ruime NL-bbox in RD.
// De buitenste ring wordt heel-NL, en de outer rings van de input worden holes.
// Resultaat: alles BUITEN de input wordt gevuld.
const NL_BBOX_RD = [
  [-50000, 250000], [400000, 250000],
  [400000, 650000], [-50000, 650000],
  [-50000, 250000],
];
function inverteerPolygon(geom) {
  if (!geom) return null;
  const holes = [];
  if (geom.type === 'Polygon') {
    if (geom.coordinates[0]) holes.push(geom.coordinates[0]);
  } else if (geom.type === 'MultiPolygon') {
    geom.coordinates.forEach(poly => {
      if (poly[0]) holes.push(poly[0]);
    });
  } else {
    return null;
  }
  return { type: 'Polygon', coordinates: [NL_BBOX_RD, ...holes] };
}

/**
 * @param {Array<{x_rd, y_rd, put_id, label?, perceel?, afgedicht?, soort?, geselecteerd?, onClick?}>} punten
 * @param {boolean} toonPutnummers  Het putnummer naast elke marker. Uit by
 *   default: op een kaart met honderden putten is dat een muur van tekst.
 * @param {Array<{type, geometrie}>} overlays  GeoJSON polygons in RD-coords
 * @param {number} hoogte
 * @param {string} defaultLayer  kaart | luchtfoto
 */
export default function RDKaart({
  punten = [], overlays = [], hoogte = 360, defaultLayer = 'kaart',
  toonPutnummers = false,
  onKaartClick = null, fitBijElkeUpdate = true, focusPunten = null,
  /* Sleutels (gebied_id, of type|naam) van gebieden die even mogen opvallen. */
  benadrukt = null,
  /* (x, y) => boolean. Levert true op plekken waar niet geklikt mag worden;
     de aanwijzer wordt daar een verbodsteken. Zonder deze prop verandert er
     niets aan het gedrag van de andere schermen. */
  verbodenOpPunt = null,
  /* {x_rd, y_rd} of null — waar het draadkruis staat. Gevoed door het zweven
     over een rij in de puttenlijst. */
  draadkruis = null,
  /* {x_rd, y_rd, nonce} of null — de kaart schuift hierheen zonder in of uit
     te zoomen. De nonce is nodig omdat twee keer dezelfde put aanklikken twee
     keer moet pannen; zonder verandert het object niet en gebeurt er niets. */
  panNaar = null,
}) {
  const containerRef = useRef(null);
  const heeftEenmaalGefitRef = useRef(false);  // voor fitBijElkeUpdate=false: track of er al 1x gefit is
  const popupRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const vectorSourceRef = useRef(null);
  const labelSourceRef = useRef(null);
  const overlaySourceRef = useRef(null);
  const overlayRef = useRef(null);

  const [actieveLaag, setActieveLaag] = useState(defaultLayer);
  const [popup, setPopup] = useState(null);

  /* De klik-handler wordt één keer opgehangen, in de init-effect hieronder. Zou
     die `onKaartClick` rechtstreeks aanroepen, dan houdt hij de versie vast van
     de allereerste render — inclusief alles waar die functie op dat moment
     overheen keek. In het puttenpaneel waren dat de gebieden en de
     registerputten, en die zijn bij de eerste render nog onderweg. Gevolg: een
     klik in een kwetsbaar gebied werd niet geweigerd, want er wérd tegen een
     lege lijst getoetst. Via deze ref roept de handler altijd de actuele versie
     aan. */
  const kaartKlikRef = useRef(onKaartClick);
  kaartKlikRef.current = onKaartClick;
  // Zelfde reden als hierboven: de pointermove-handler hangt er ook maar één keer.
  const verbodenRef = useRef(verbodenOpPunt);
  verbodenRef.current = verbodenOpPunt;
  // Idem voor de overlay-opbouw, die alleen bij nieuwe gebieden draait.
  const benadruktRef = useRef(benadrukt);
  benadruktRef.current = benadrukt;

  // Init kaart één keer
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const brt = new TileLayer({
      source: makeSource('brt/achtergrondkaart/wmts/v2_0', 'standaard', 'png'),
    });
    const bgt = new TileLayer({
      source: makeSource('lv/bgt/wmts/v1_0', 'achtergrondvisualisatie', 'png'),
      minZoom: BGT_MIN_ZOOM,  // verschijnt vanaf zoom 12 — dekt BRT af op detail-niveau
    });
    const luchtfoto = new TileLayer({
      source: makeSource('hwh/luchtfotorgb/wmts/v1_0', 'Actueel_orthoHR', 'jpeg'),
      visible: false,
    });
    layersRef.current = { brt, bgt, luchtfoto };

    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({ source: vectorSource });

    // Onder de markers, zodat een label nooit over een stip valt.
    const labelSource = new VectorSource();
    const labelLayer = new VectorLayer({ source: labelSource, declutter: true });

    const overlaySource = new VectorSource();
    const overlayLayer = new VectorLayer({
      source: overlaySource,
      // gebieden onder de marker-layer
    });

    const overlay = new Overlay({
      element: popupRef.current,
      offset: [0, -14],
      positioning: 'bottom-center',
      stopEvent: false,
    });

    const map = new Map({
      target: containerRef.current,
      layers: [brt, bgt, luchtfoto, overlayLayer, labelLayer, vectorLayer],
      view: new View({
        projection: rdProjection,
        center: [155000, 463000],
        zoom: 3,
      }),
      overlays: [overlay],
      controls: [],
    });

    /* layerFilter: alleen de markerlaag telt als "je klikte op een put".
       Zonder die filter vond forEachFeatureAtPixel ook de gebiedspolygonen —
       een klik binnen een watergang of waterkering gold dan als een klik op
       een feature, en onKaartClick werd nooit aangeroepen. Je kon daar dus
       geen put prikken, terwijl dat handmatig gewoon mag. */
    map.on('click', (evt) => {
      const f = map.forEachFeatureAtPixel(evt.pixel, (feat) => feat,
        { layerFilter: (laag) => laag === vectorLayer });
      if (f) {
        const data = f.get('data');
        if (data?.onClick) data.onClick(data);
        else {
          overlay.setPosition(f.getGeometry().getCoordinates());
          setPopup(data);
        }
      } else {
        overlay.setPosition(undefined);
        setPopup(null);
        // Additief: alleen actief als een aanroeper expliciet onKaartClick meegeeft.
        // evt.coordinate is al RD (EPSG:28992) -> [x_rd, y_rd].
        if (typeof kaartKlikRef.current === 'function') {
          kaartKlikRef.current(evt.coordinate[0], evt.coordinate[1]);
        }
      }
    });

    /* De aanwijzer vertelt wat een klik gaat doen. Boven een put: een hand.
       Boven een plek waar geen put mag: een verbodsteken — anders klik je daar
       en gebeurt er niets, en dat leest als een kapotte kaart. Alleen de
       markerlaag telt als "hier zit iets"; over een gebiedsvlak heen hoefde de
       hand al niet meer te verschijnen sinds een klik daar gewoon een klik op
       de kaart is. */
    map.on('pointermove', (evt) => {
      const target = map.getTargetElement();
      if (map.forEachFeatureAtPixel(evt.pixel, () => true,
        { layerFilter: (laag) => laag === vectorLayer })) {
        target.style.cursor = 'pointer';
        return;
      }
      const verboden = verbodenRef.current;
      target.style.cursor = (typeof verboden === 'function'
        && verboden(evt.coordinate[0], evt.coordinate[1])) ? 'not-allowed' : '';
    });

    mapRef.current = map;
    vectorSourceRef.current = vectorSource;
    labelSourceRef.current = labelSource;
    overlaySourceRef.current = overlaySource;
    overlayRef.current = overlay;

    return () => {
      map.setTarget(null);
      mapRef.current = null;
    };
  }, []);

  // Modus wisselen → toggle visibility
  useEffect(() => {
    const { brt, bgt, luchtfoto } = layersRef.current;
    if (!brt) return;
    if (actieveLaag === 'kaart') {
      brt.setVisible(true);
      bgt.setVisible(true);  // BGT komt automatisch in beeld vanaf zoom 12
      luchtfoto.setVisible(false);
    } else {
      brt.setVisible(false);
      bgt.setVisible(false);
      luchtfoto.setVisible(true);
    }
  }, [actieveLaag]);

  // Overlays (gebieden) updaten
  useEffect(() => {
    if (!overlaySourceRef.current) return;
    overlaySourceRef.current.clear();
    if (!overlays || overlays.length === 0) return;

    const fmt = new GeoJSON({ dataProjection: rdProjection, featureProjection: rdProjection });

    // Sorteer: gewone gebieden eerst, ambtsgebied (inverse mask) bovenop, zodat
    // de mask alle andere overlays buiten het ambtsgebied bedekt met grijs.
    const gesorteerd = [...overlays].sort((a, b) => {
      const aMask = gebiedStijl(a.type).inverse ? 1 : 0;
      const bMask = gebiedStijl(b.type).inverse ? 1 : 0;
      return aMask - bMask;
    });

    gesorteerd.forEach(g => {
      try {
        const stijl = gebiedStijl(g.type);
        let geometry = g.geometrie;
        if (stijl.inverse && geometry) {
          geometry = inverteerPolygon(geometry);
        }
        if (!geometry) return;
        const sleutel = String(g.gebied_id ?? `${g.type}|${g.naam || ''}`);
        const features = fmt.readFeatures({
          type: 'Feature',
          geometry,
          properties: { type: g.type, naam: g.naam, sleutel },
        });
        features.forEach(f => f.setStyle(
          (benadruktRef.current || []).includes(sleutel)
            ? gebiedStyleBenadrukt(g.type)
            : gebiedStyle(g.type)
        ));
        overlaySourceRef.current.addFeatures(features);
      } catch (e) {
        console.warn('Kon overlay niet renderen:', e);
      }
    });
  }, [overlays]);

  /* Benadrukken zonder de overlays opnieuw op te bouwen: alleen de stijl van de
     betrokken vlakken wisselt. Opnieuw inlezen zou bij elke muisbeweging de
     hele geometrie door de GeoJSON-parser halen. */
  useEffect(() => {
    const bron = overlaySourceRef.current;
    if (!bron) return;
    const aan = benadrukt || [];
    bron.getFeatures().forEach((f) => {
      const sleutel = f.get('sleutel');
      const type = f.get('type');
      if (!type) return;
      f.setStyle(sleutel && aan.includes(sleutel) ? gebiedStyleBenadrukt(type) : gebiedStyle(type));
    });
  }, [benadrukt, overlays]);

  // Markers updaten
  useEffect(() => {
    if (!vectorSourceRef.current || !mapRef.current) return;
    vectorSourceRef.current.clear();
    if (labelSourceRef.current) labelSourceRef.current.clear();
    if (punten.length === 0) return;

    const features = punten.map(p => {
      const f = new Feature({ geometry: new Point([Number(p.x_rd), Number(p.y_rd)]) });
      f.set('data', p);
      f.setStyle(markerStyle(p.afgedicht, p.soort, p.geselecteerd));
      return f;
    });
    vectorSourceRef.current.addFeatures(features);

    if (toonPutnummers && labelSourceRef.current) {
      labelSourceRef.current.addFeatures(punten.map(p => {
        const f = new Feature({ geometry: new Point([Number(p.x_rd), Number(p.y_rd)]) });
        // Zelfde schrijfwijze als in de tabellen en in de popup: zonder streepje.
        f.setStyle(labelStijl(p.label || (p.put_id || '').replace('-', '')));
        return f;
      }));
    }

    // View-aanpassing. Default (fitBijElkeUpdate=true): exact zoals voorheen,
    // bij elke punten-wijziging opnieuw fitten. Bij fitBijElkeUpdate=false:
    // alleen de eerste keer dat er punten zijn (daarna viewport vasthouden).
    const magFitten = fitBijElkeUpdate || !heeftEenmaalGefitRef.current;
    if (magFitten) {
      const view = mapRef.current.getView();
      // focusPunten gevuld -> eenmalig op die subset inzoomen i.p.v. op alles.
      const focusGeldig = Array.isArray(focusPunten) && focusPunten.length > 0;
      if (focusGeldig && !heeftEenmaalGefitRef.current) {
        const xs = focusPunten.map(fp => Number(fp.x_rd));
        const ys = focusPunten.map(fp => Number(fp.y_rd));
        const extent = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
        if (focusPunten.length === 1) {
          view.setCenter([xs[0], ys[0]]);
          view.setZoom(13);
        } else {
          view.fit(extent, { padding: [60, 60, 60, 60], maxZoom: 13 });
        }
      } else if (features.length === 1) {
        view.setCenter([Number(punten[0].x_rd), Number(punten[0].y_rd)]);
        view.setZoom(13);
      } else {
        view.fit(vectorSourceRef.current.getExtent(), {
          padding: [40, 40, 40, 40],
          maxZoom: 13,
        });
      }
      heeftEenmaalGefitRef.current = true;
    }
  }, [punten, fitBijElkeUpdate, focusPunten, toonPutnummers]);

  /* Het draadkruis in dezelfde bron als de markers, maar als eigen feature met
     een vaste sleutel. Zo hoeft er geen extra laag bij, en verdwijnt hij
     vanzelf zodra de markers opnieuw worden gezet — waarna dit effect hem
     terugzet als er nog steeds ergens overheen wordt gezweefd. */
  useEffect(() => {
    const bron = vectorSourceRef.current;
    if (!bron) return;
    bron.getFeatures().filter((f) => f.get('draadkruis')).forEach((f) => bron.removeFeature(f));
    if (!draadkruis || draadkruis.x_rd == null || draadkruis.y_rd == null) return;
    const f = new Feature({
      geometry: new Point([Number(draadkruis.x_rd), Number(draadkruis.y_rd)]),
    });
    f.set('draadkruis', true);
    f.setStyle(draadkruisStijl());
    bron.addFeature(f);
  }, [draadkruis, punten]);

  /* Schuiven naar een put. Alleen het middelpunt: de zoom die de gebruiker
     zelf heeft gekozen blijft staan, anders springt de kaart onder je handen
     weg bij elke klik in de lijst. */
  useEffect(() => {
    if (!panNaar || !mapRef.current) return;
    if (panNaar.x_rd == null || panNaar.y_rd == null) return;
    mapRef.current.getView().animate({
      center: [Number(panNaar.x_rd), Number(panNaar.y_rd)],
      duration: 320,
    });
  }, [panNaar]);

  const lagen = [
    { id: 'kaart', label: 'Kaart' },
    { id: 'luchtfoto', label: 'Luchtfoto' },
  ];

  return (
    <div style={{ position: 'relative', borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }}>
      <div ref={containerRef} style={{ height: hoogte, background: 'var(--surface-3)' }} />

      <div style={{
        position: 'absolute', top: 12, right: 12,
        background: 'var(--surface)',
        borderRadius: 999,
        padding: 4,
        boxShadow: 'var(--shadow-card)',
        display: 'flex', gap: 2,
        zIndex: 5,
      }}>
        {lagen.map(l => (
          <button
            key={l.id}
            onClick={() => setActieveLaag(l.id)}
            style={{
              border: 0, cursor: 'pointer',
              borderRadius: 999, padding: '6px 14px',
              fontSize: 12.5, fontWeight: 500,
              fontFamily: 'inherit',
              background: actieveLaag === l.id ? 'var(--text)' : 'transparent',
              color: actieveLaag === l.id ? '#fff' : 'var(--text-soft)',
              transition: 'background .15s, color .15s',
            }}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div ref={popupRef} style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-card)',
        padding: popup ? '10px 12px' : 0,
        fontSize: 13,
        minWidth: popup ? 140 : 0,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        display: popup ? 'block' : 'none',
      }}>
        {popup && (
          <>
            <div style={{ fontWeight: 600 }}>
              {popup.label || (popup.put_id || '').replace('-', '')}
            </div>
            <div style={{ color: 'var(--text-muted)', fontFamily: 'SF Mono, monospace', fontSize: 12, marginTop: 2 }}>
              {Math.round(popup.x_rd)}, {Math.round(popup.y_rd)}
            </div>
            {popup.perceel && (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                {popup.perceel}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{
        position: 'absolute', bottom: 4, right: 8,
        fontSize: 10, color: 'var(--text-muted)',
        background: 'rgba(255,255,255,0.7)', padding: '1px 6px',
        borderRadius: 4, pointerEvents: 'auto', zIndex: 5,
      }}
        dangerouslySetInnerHTML={{ __html: ATTRIBUTION }}
      />
    </div>
  );
}
